import React, { useEffect, useMemo, useState } from "react";
import { hqApi } from "@/hq/hqApi";
import { Card, Skeleton } from "@/hq/HQPrimitives";
import { toast } from "sonner";
import { asText } from "@/hq/asText";
import {
  X, Send, Save, FlaskConical, Code2, Monitor, Smartphone,
  AlertTriangle, ChevronDown, CheckCircle2, Copy,
} from "lucide-react";

const APPROVED = [
  { key: "CONTACT_NAME",     hint: "Recipient first name (falls back to \"there\")" },
  { key: "BUSINESS_NAME",    hint: "Prospect business — validated before send" },
  { key: "INTEREST_CTA_URL", hint: "Unique tracked CTA per recipient" },
  { key: "SENDER_NAME",      hint: "Logged-in HQ operator name" },
  { key: "ORANGE_LOGO",      hint: "Canonical OrangeBySite email logo" },
];

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`px-3 py-1.5 rounded-full text-[11px] tracking-[0.16em] uppercase font-black border transition-colors ${active ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#4A4A4A] border-[#EAEAEA] hover:border-[#0A0A0A]"}`}>{children}</button>
  );
}

/**
 * Phase 3I.4T — Bulk Follow-Up Generator.
 * Reuses the exact same sanitiser/tracking/idempotency/brand-kit as the
 * individual FollowUpGeneratorModal; only the fan-out is server-side.
 */
export default function BulkFollowUpGeneratorModal({ selectedIds, onClose, onSent }) {
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [tab, setTab] = useState("code");
  const [previewFor, setPreviewFor] = useState(null);   // communication_id
  const [preview, setPreview] = useState(null);         // {html, subject, business_name, ...}
  const [previewError, setPreviewError] = useState(null);
  const [validation, setValidation] = useState(null);    // {ready, blocked, recipients}
  const [busy, setBusy] = useState(false);
  const [testRecipient, setTestRecipient] = useState("");
  const [showTest, setShowTest] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [results, setResults] = useState(null);
  const [showRecipients, setShowRecipients] = useState(false);

  const batchId = useMemo(
    () => `bulk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
    []
  );

  useEffect(() => {
    hqApi.get("/auth/me")
      .then((r) => { if (r.data?.email) setTestRecipient(r.data.email); })
      .catch(() => {});
  }, []);

  const validate = async () => {
    if (!subject.trim() || !html.trim()) { toast.error("Subject + HTML required"); return null; }
    setBusy(true);
    try {
      const r = await hqApi.post("/control/pitches/bulk-followup/validate",
                                    { subject, html, communication_ids: selectedIds });
      setValidation(r.data);
      if (!previewFor && r.data.recipients?.length) {
        const firstEligible = r.data.recipients.find((x) => x.eligible)
          || r.data.recipients[0];
        setPreviewFor(firstEligible.communication_id);
      }
      return r.data;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Validation failed"));
      return null;
    } finally { setBusy(false); }
  };

  const runPreview = async (recipientId, { autoSwitch = true } = {}) => {
    if (!recipientId) return;
    if (!subject.trim() || !html.trim()) return;
    setBusy(true); setPreviewError(null);
    try {
      const r = await hqApi.post("/control/pitches/bulk-followup/preview",
                                    { subject, html, communication_id: recipientId });
      setPreview(r.data);
      if (autoSwitch) setTab("desktop");
    } catch (e) {
      const d = e.response?.data?.detail;
      setPreviewError(typeof d === "string" ? d : (d?.message || JSON.stringify(d) || "Preview failed"));
      setPreview(null);
    } finally { setBusy(false); }
  };

  const render = async () => {
    const v = await validate();
    if (v && previewFor) await runPreview(previewFor);
  };

  const sendTest = async () => {
    if (!subject.trim() || !html.trim()) { toast.error("Subject + HTML required"); return; }
    if (!testRecipient.includes("@")) { toast.error("Valid test email required"); return; }
    if (!previewFor) { toast.error("Pick a preview recipient first"); return; }
    setBusy(true);
    try {
      const r = await hqApi.post("/control/pitches/bulk-followup/send-test",
                                    { subject, html, communication_id: previewFor,
                                      test_recipient: testRecipient });
      if (r.data.status === "sent")
        toast.success(`Test sent to ${testRecipient} (personalised for ${r.data.personalised_for})`);
      else if (r.data.status === "skipped")
        toast.info("Test send skipped (email provider not configured)");
      else toast.error("Test send failed");
      setShowTest(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Test send failed"));
    } finally { setBusy(false); }
  };

  const sendBulk = async () => {
    setBusy(true);
    try {
      const eligibleIds = (validation?.recipients || [])
        .filter((r) => r.eligible)
        .map((r) => r.communication_id);
      if (eligibleIds.length === 0) { toast.error("No eligible recipients"); return; }
      const r = await hqApi.post("/control/pitches/bulk-followup/send", {
        subject, html, communication_ids: eligibleIds,
        batch_id: batchId, confirm: true,
      });
      setResults(r.data);
      onSent?.();
      setShowConfirm(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Bulk send failed"));
    } finally { setBusy(false); }
  };

  const readyCount = validation?.ready ?? selectedIds.length;
  const blockedCount = validation?.blocked ?? 0;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center p-2 md:p-6 overflow-y-auto"
         onClick={onClose} data-testid="bulk-followup-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl my-4"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[#EAEAEA]">
          <div>
            <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#FF6A00]">Bulk Follow-Up Generator</div>
            <h2 className="mt-1 font-black text-2xl tracking-[-0.02em]" data-testid="bulk-followup-title">
              {selectedIds.length} selected prospects
            </h2>
            <button type="button" onClick={() => setShowRecipients((v) => !v)}
                    className="mt-2 text-[11px] tracking-[0.14em] uppercase font-black text-[#8A8A8A] hover:text-[#0A0A0A] inline-flex items-center gap-1"
                    data-testid="bulk-followup-toggle-recipients">
              <ChevronDown size={12}/> {showRecipients ? "Hide" : "Show"} recipient list
            </button>
          </div>
          <button data-testid="bulk-followup-close" onClick={onClose}
                  className="p-2 hover:bg-[#F6F5F2] rounded-full"><X size={20}/></button>
        </div>

        {showRecipients && (
          <div className="px-5 pt-3 max-h-[220px] overflow-y-auto" data-testid="bulk-followup-recipients-list">
            <table className="w-full text-[12px]">
              <thead className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A]">
                <tr><th className="text-left py-1">Business</th><th className="text-left py-1">Contact</th><th className="text-left py-1">Email</th><th className="text-left py-1">Status</th></tr>
              </thead>
              <tbody>
                {(validation?.recipients || selectedIds.map((id) => ({ communication_id: id }))).map((r) => (
                  <tr key={r.communication_id} className="border-t border-[#EAEAEA]">
                    <td className="py-1">{asText(r.business_name)}</td>
                    <td>{asText(r.contact_name)}</td>
                    <td>{asText(r.email)}</td>
                    <td>{r.eligible === false
                      ? <span className="text-[#C1350B] font-bold">{r.reason || "ineligible"}</span>
                      : r.eligible ? <span className="text-[#0F7B3E] font-bold">ready</span> : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Body */}
        <div className="p-5 grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-3 space-y-4">
            <div>
              <label className="text-[10px] tracking-[0.24em] uppercase font-black text-[#FF6A00]">Subject</label>
              <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                     placeholder="A private platform for {{BUSINESS_NAME}}"
                     data-testid="bulk-followup-subject-input"
                     className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EAEAEA] focus:outline-none focus:border-[#FF6A00] text-[14px]"/>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#FF6A00]">Email design</div>
              <div className="flex gap-2">
                <TabBtn active={tab === "code"}    onClick={() => setTab("code")}   testId="bulk-followup-tab-code"><Code2 size={12} className="inline mr-1"/>Code</TabBtn>
                <TabBtn active={tab === "desktop"} onClick={() => { setTab("desktop"); if (!preview) render(); }} testId="bulk-followup-tab-desktop"><Monitor size={12} className="inline mr-1"/>Desktop</TabBtn>
                <TabBtn active={tab === "mobile"}  onClick={() => { setTab("mobile"); if (!preview) render(); }}  testId="bulk-followup-tab-mobile"><Smartphone size={12} className="inline mr-1"/>Mobile</TabBtn>
              </div>
            </div>

            {tab === "code" ? (
              <div className="space-y-2">
                <textarea value={html} onChange={(e) => setHtml(e.target.value)}
                          placeholder="<html>…paste bulk master HTML with {{CONTACT_NAME}}, {{BUSINESS_NAME}}, {{INTEREST_CTA_URL}}…"
                          data-testid="bulk-followup-html-editor" spellCheck={false}
                          className="w-full font-mono text-[12px] p-3 rounded-lg border border-[#EAEAEA] focus:outline-none focus:border-[#FF6A00] min-h-[420px] leading-relaxed"/>
                {previewError && (
                  <div className="p-3 rounded-lg bg-[#FDECEC] border border-[#F5B7B1] text-[12px] text-[#8A1E12] flex items-start gap-2"
                       data-testid="bulk-followup-render-error">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0"/>
                    <div><div className="font-black">Unable to render preview</div><div>{previewError}</div></div>
                  </div>
                )}
                <div className="flex justify-end">
                  <button type="button" onClick={render} disabled={busy}
                          data-testid="bulk-followup-render-btn"
                          className="px-3 py-2 rounded-full bg-white border border-[#0A0A0A] text-[11px] font-black tracking-[0.16em] uppercase text-[#0A0A0A] disabled:opacity-40">
                    {busy ? "Rendering…" : "Render HTML"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Recipient selector for personalised preview */}
                <div className="flex items-center gap-2">
                  <label className="text-[10px] tracking-[0.14em] uppercase font-bold text-[#8A8A8A]">Previewing:</label>
                  <select value={previewFor || ""} onChange={(e) => { setPreviewFor(e.target.value); runPreview(e.target.value, { autoSwitch: false }); }}
                          data-testid="bulk-followup-preview-recipient"
                          className="text-[12px] px-2 py-1 rounded-md border border-[#EAEAEA] focus:outline-none focus:border-[#FF6A00]">
                    {(validation?.recipients || []).filter((r) => r.eligible).map((r) => (
                      <option key={r.communication_id} value={r.communication_id}>
                        {r.business_name} — {r.email}
                      </option>
                    ))}
                  </select>
                </div>
                {!preview ? <Skeleton h={420}/> : (
                  <div className={`mx-auto ${tab === "desktop" ? "max-w-[720px]" : "max-w-[390px]"}`}>
                    <div className="text-[11px] text-[#8A8A8A] mb-1">
                      Subject: <span className="font-black text-[#0A0A0A]">{preview.subject}</span>
                    </div>
                    <iframe title={`preview ${previewFor}`} srcDoc={preview.html} sandbox=""
                            data-testid={`bulk-followup-preview-${tab}`}
                            className="w-full h-[520px] rounded-xl border border-[#EAEAEA] bg-white"/>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <Card className="p-3">
              <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#FF6A00] mb-2">Placeholders</div>
              {APPROVED.map((p) => (
                <div key={p.key} className="py-1 border-b border-dashed border-[#EAEAEA]">
                  <div className="font-mono text-[11px]">{`{{${p.key}}}`}</div>
                  <div className="text-[10px] text-[#8A8A8A]">{p.hint}</div>
                </div>
              ))}
            </Card>

            {validation && (
              <Card className="p-3 space-y-1" data-testid="bulk-followup-validation">
                <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#0F7B3E]">
                  Ready to send: {readyCount}
                </div>
                {blockedCount > 0 && (
                  <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#C1350B]"
                       data-testid="bulk-followup-blocked-count">
                    Needs attention: {blockedCount}
                  </div>
                )}
              </Card>
            )}

            <div className="space-y-2">
              <button type="button" onClick={() => setShowTest(true)} disabled={busy}
                      data-testid="bulk-followup-send-test-btn"
                      className="w-full px-3 py-2 rounded-full bg-white border border-[#0A0A0A] text-[11px] font-black tracking-[0.16em] uppercase text-[#0A0A0A] disabled:opacity-40 inline-flex items-center justify-center gap-2">
                <FlaskConical size={13}/> Send test
              </button>
              <button type="button" onClick={async () => { const v = await validate(); if (v && v.ready > 0) setShowConfirm(true); }}
                      disabled={busy || !subject.trim() || !html.trim()}
                      data-testid="bulk-followup-send-btn"
                      className="w-full px-3 py-2 rounded-full bg-[#FF6A00] text-white text-[11px] font-black tracking-[0.16em] uppercase disabled:opacity-40 inline-flex items-center justify-center gap-2">
                <Send size={13}/> Send bulk follow-up
              </button>
            </div>
          </div>
        </div>

        {showTest && (
          <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4"
               onClick={() => setShowTest(false)}>
            <div className="bg-white rounded-2xl p-5 w-full max-w-md"
                 onClick={(e) => e.stopPropagation()} data-testid="bulk-followup-test-prompt">
              <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#FF6A00]">Send test — personalised for the preview recipient</div>
              <p className="text-[12px] text-[#4A4A4A] mt-1">Subject prefixed with [TEST]. Never touches the real prospect record.</p>
              <input type="email" value={testRecipient} onChange={(e) => setTestRecipient(e.target.value)}
                     data-testid="bulk-followup-test-recipient-input"
                     className="mt-3 w-full px-3 py-2 rounded-lg border border-[#EAEAEA] focus:outline-none focus:border-[#FF6A00] text-[13px]"/>
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setShowTest(false)} className="px-3 py-2 rounded-full border border-[#EAEAEA] text-[11px] font-black tracking-[0.16em] uppercase text-[#4A4A4A]">Cancel</button>
                <button onClick={sendTest} disabled={busy} data-testid="bulk-followup-test-confirm-btn"
                        className="px-3 py-2 rounded-full bg-[#0A0A0A] text-white text-[11px] font-black tracking-[0.16em] uppercase disabled:opacity-40">Send test</button>
              </div>
            </div>
          </div>
        )}

        {showConfirm && (
          <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4"
               onClick={() => setShowConfirm(false)}>
            <div className="bg-white rounded-2xl p-5 w-full max-w-md"
                 onClick={(e) => e.stopPropagation()} data-testid="bulk-followup-send-confirm">
              <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#FF6A00]">Ready to send</div>
              <div className="mt-2 text-4xl font-black tracking-[-0.02em]">{readyCount} follow-up emails</div>
              <ul className="mt-3 text-[12px] text-[#4A4A4A] space-y-1">
                <li>✓ Business names resolved</li>
                <li>✓ Contact names resolved / fallback applied</li>
                <li>✓ Subjects resolved</li>
                <li>✓ HTML validated</li>
                <li>✓ Unique CTA links prepared</li>
                <li>✓ Tracking enabled</li>
                <li>✓ Test records excluded</li>
              </ul>
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={() => setShowConfirm(false)} className="px-3 py-2 rounded-full border border-[#EAEAEA] text-[11px] font-black tracking-[0.16em] uppercase text-[#4A4A4A]">Cancel</button>
                <button onClick={sendBulk} disabled={busy}
                        data-testid="bulk-followup-send-confirm-btn"
                        className="px-3 py-2 rounded-full bg-[#FF6A00] text-white text-[11px] font-black tracking-[0.16em] uppercase disabled:opacity-40">
                  Confirm & send {readyCount} follow-ups
                </button>
              </div>
            </div>
          </div>
        )}

        {results && (
          <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4"
               onClick={() => setResults(null)}>
            <div className="bg-white rounded-2xl p-5 w-full max-w-lg"
                 onClick={(e) => e.stopPropagation()} data-testid="bulk-followup-results">
              <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#FF6A00]">Bulk follow-up</div>
              <div className="mt-1 text-2xl font-black tracking-[-0.02em]">{results.total} recipients</div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="rounded-xl border border-[#B7EBCF] bg-[#EBFBF2] p-3">
                  <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-[#0F7B3E]">Sent</div>
                  <div className="text-2xl font-black" data-testid="bulk-results-sent">{results.counts?.sent || 0}</div>
                </div>
                <div className="rounded-xl border border-[#F5C4C4] bg-[#FFECEC] p-3">
                  <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-[#C1350B]">Failed</div>
                  <div className="text-2xl font-black" data-testid="bulk-results-failed">{results.counts?.failed || 0}</div>
                </div>
                <div className="rounded-xl border border-[#EAEAEA] bg-[#F6F5F2] p-3">
                  <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-[#8A8A8A]">Skipped</div>
                  <div className="text-2xl font-black" data-testid="bulk-results-skipped">{results.counts?.skipped || 0}</div>
                </div>
              </div>
              <div className="mt-5 flex justify-end">
                <button onClick={() => { setResults(null); onClose?.(); }}
                        className="px-3 py-2 rounded-full bg-[#0A0A0A] text-white text-[11px] font-black tracking-[0.16em] uppercase">Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
